import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import {
  FluentProvider,
  webLightTheme,
  webDarkTheme,
  makeStyles,
  tokens,
  Title1,
  Subtitle2,
  Button,
  Spinner,
} from '@fluentui/react-components';
import { ChatContainer } from './components/ChatContainer';
import ConnectionIndicator from './components/ConnectionIndicator';
import { ThemeSelector } from './components/ThemeSelector';
import { AuthenticationButton } from './components/AuthenticationButton';
import { RoleGuard } from './components/RoleGuard';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ProtectedRoute } from './components/ProtectedRoute';
import { DocumentUpload } from './components/DocumentUpload';
import { SummaryPdfViewer } from './components/SummaryPdfViewer';
import { Navigation } from './components/Navigation';
import { AuthProvider, useAuth } from './auth/AuthProvider';
import { ThemeContext } from './utils/theme';
import { AppTheme, DEFAULT_THEMES } from './models/types';
import { getStoredTheme, storeTheme, getThemeCSSProps } from './utils/theme';
import { ChatAPI } from './utils/api';

const useStyles = makeStyles({
  root: {
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: 'var(--color-background)',
    color: 'var(--color-text-primary)',
  },
  header: {
    padding: tokens.spacingVerticalM,
    borderBottom: `1px solid var(--color-border)`,
    backgroundColor: 'var(--color-surface)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  headerContent: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacingVerticalXS,
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
  },
  main: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  subNav: {
    display: 'flex',
    alignItems: 'stretch',
    height: '40px',
    backgroundColor: 'var(--color-surface)',
    borderBottom: '1px solid var(--color-border)',
    flexShrink: 0,
    paddingLeft: '16px',
  },
  subNavTab: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    padding: '0 20px',
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '500',
    fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
    color: '#64748B',
    transition: 'color 0.15s ease',
    ':hover': {
      color: '#1E293B',
    },
  },
  subNavTabActive: {
    color: '#0EA5E9',
    fontWeight: '600',
    '::after': {
      content: '""',
      position: 'absolute',
      bottom: '0',
      left: '0',
      right: '0',
      height: '2px',
      backgroundColor: '#0EA5E9',
      borderRadius: '2px 2px 0 0',
    },
  },
  // Connection status styles removed - now handled in ChatInterface
});

/**
 * Main application content that requires authentication context
 */
const AppContent: React.FC = () => {
  const classes = useStyles();
  const { isAuthenticated, getToken, loginInProgress } = useAuth();
  const [currentTheme, setCurrentTheme] = useState<AppTheme>(getStoredTheme);
  const [connectionStatus, setConnectionStatus] = useState<'checking' | 'connected' | 'disconnected'>('checking');
  const [agentInfo, setAgentInfo] = useState<any>(null);
  const [selectedView, setSelectedView] = useState<string>('Authorizations');

  console.log(`[App.tsx] Current selectedView state: ${selectedView}`);

  /**
   * Set up authentication provider for API calls.
   * 
   * CRITICAL: This must be in a useEffect to avoid race conditions on page refresh.
   * 
   * Why useEffect is required:
   * - When a user directly navigates to /documents (or any protected route) and refreshes,
   *   child components like DocumentUpload mount and immediately fire their useEffects
   * - If ChatAPI.setAuthProvider runs synchronously during render (outside useEffect),
   *   there's a race condition where child component API calls may execute before the
   *   auth provider is properly wired up
   * - By placing this in useEffect, we guarantee the auth provider is set up AFTER
   *   component mount but BEFORE child effects run (React effect execution order)
   * 
   * The dependency on [getToken] ensures:
   * - Updates when authentication state changes (login/logout)
   * - Provides fresh token getter with latest auth context
   * 
   * Note: AuthProvider.tsx also sets an auth provider at line 373, but this overwrites
   * it with the full getToken function that includes better error handling and user
   * signout checks from the AuthContext.
   */
  useEffect(() => {
    ChatAPI.setAuthProvider({ getToken });
  }, [getToken]);

  // Check backend connection on mount and when authentication changes
  useEffect(() => {
    const checkConnection = async () => {
      // Skip health check if user has explicitly signed out
      if (sessionStorage.getItem('msal_explicit_signout')) {
        console.log('[App] Skipping health check - user has signed out');
        setConnectionStatus('disconnected');
        return;
      }

      setConnectionStatus('checking');
      try {
        const health = await ChatAPI.healthCheck();
        setConnectionStatus(health.status === 'healthy' ? 'connected' : 'disconnected');
        
        if (health.status === 'healthy') {
          setAgentInfo({
            name: 'Aurora - v0.2',
            description: 'AI Assistant for medical authorization management'
          });
        }
      } catch (error) {
        console.error('Health check failed:', error);
        setConnectionStatus('disconnected');
      }
    };

    // Only run health check if we're not during logout process
    if (!loginInProgress) {
      checkConnection();
    }
  }, [isAuthenticated, loginInProgress]); // Re-check when authentication status changes

  const handleThemeChange = (theme: AppTheme) => {
    setCurrentTheme(theme);
    storeTheme(theme);
  };

  const fluentTheme = currentTheme.isDark ? webDarkTheme : webLightTheme;

  // Apply CSS custom properties
  useEffect(() => {
    const props = getThemeCSSProps(currentTheme);
    Object.entries(props).forEach(([key, value]) => {
      document.documentElement.style.setProperty(key, value);
    });
  }, [currentTheme]);

  const themeContextValue = {
    currentTheme,
    themes: DEFAULT_THEMES,
    setTheme: handleThemeChange,
  };

  // Connection status functions removed - now handled in ChatInterface

  return (
    <FluentProvider theme={fluentTheme}>
      <ThemeContext.Provider value={themeContextValue}>
        <div className={classes.root}>
          {/* Header */}
          <header className={classes.header}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM }}>         
                <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS }}>
                  <img 
                    src="/aurora-logo.png" 
                    alt="Aurora Logo" 
                    style={{ height: '44px', width: 'auto' }}
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                  />
                  <div className={classes.headerContent}>
                    <Title1>
                      {agentInfo?.name || 'Aurora AI'}
                    </Title1>
                  </div>
                </div>
                {isAuthenticated && <Navigation />}
              </div>
              <div className={classes.headerActions}>
                <ConnectionIndicator />
                <AuthenticationButton />
                <ThemeSelector />
              </div>
            </div>
          </header>

          {/* Sub-nav view selector */}
          {/* TODO (accessibility enhancement): These buttons behave as tabs but don't expose
              selection state to assistive tech. Consider adding role="tablist" / role="tab"
              with aria-selected, or aria-current="page", so screen readers can announce the
              active view (WCAG 2.1 SC 4.1.2). */}
          <nav className={classes.subNav}>
            {['Authorizations', 'Appeals'].map((view) => (
              <button
                key={view}
                className={`${classes.subNavTab} ${selectedView === view ? classes.subNavTabActive : ''}`}
                onClick={() => setSelectedView(view)}
              >
                {view}
              </button>
            ))}
          </nav>

          {/* Connection Status removed - now integrated in ChatInterface */}

          {/* Main Content with Routes */}
          <main className={classes.main}>
            <Routes>
              <Route 
                path="/" 
                element={
                  <ProtectedRoute>
                    {connectionStatus === 'connected' ? (
                      <RoleGuard>
                        <ErrorBoundary fallback={
                          <div style={{ 
                            padding: tokens.spacingVerticalL, 
                            textAlign: 'center' 
                          }}>
                            <Title1>Chat Interface Error</Title1>
                            <Subtitle2>
                              There was an error loading the chat interface. Please refresh the page.
                            </Subtitle2>
                          </div>
                        }>
                          <ChatContainer 
                            uiMode={selectedView} 
                            onViewChange={setSelectedView}
                          />
                        </ErrorBoundary>
                      </RoleGuard>
                    ) : connectionStatus === 'disconnected' ? (
                      <div style={{ 
                        padding: tokens.spacingVerticalL, 
                        textAlign: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: tokens.spacingVerticalM,
                      }}>
                        <Title1>Backend Unavailable</Title1>
                        <Subtitle2>
                          The Aurora AI backend is currently unavailable. Please try again shortly.
                        </Subtitle2>
                        <Button 
                          appearance="primary"
                          onClick={() => window.location.reload()}
                        >
                          Retry Connection
                        </Button>
                      </div>
                    ) : (
                      <div style={{ 
                        padding: tokens.spacingVerticalL, 
                        textAlign: 'center',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                        <Spinner size="large" label="Connecting to backend..." />
                      </div>
                    )}
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/documents" 
                element={
                  <ProtectedRoute>
                    <DocumentUpload selectedView={selectedView} />
                  </ProtectedRoute>
                } 
              />
              <Route
                path="/documents/summary/ccr/:summaryId"
                element={
                  <ProtectedRoute>
                    <SummaryPdfViewer summaryType="ccr" />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/documents/summary/authorization/:summaryId"
                element={
                  <ProtectedRoute>
                    <SummaryPdfViewer summaryType="authorization" />
                  </ProtectedRoute>
                }
              />
            </Routes>
          </main>
        </div>
      </ThemeContext.Provider>
    </FluentProvider>
  );
};

/**
 * Main App component with authentication provider, router, and error boundary
 */
export const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
};

export default App;
